// =============================================================================
// PAGE ANAH SYNC - Hub Conformité > Aides ANAH
// Migré vers OHGrid — GROOT: UI-004 (stats cliquables), UI-005 (tri desc),
// UI-006 (actions par ligne), UI-007 (tri cliquable), UI-002 (sélection multiple)
// =============================================================================

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  RefreshCw, Building2, Clock, CheckCircle, XCircle, AlertTriangle, Info,
  FileText, Settings, Loader2, ExternalLink, Calendar, Activity, Download,
  Edit3, Save, Phone, Euro, FolderPlus, Eye,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { OHGrid } from "../../components/ui/oh-grid";
import { formatDateFR } from "../../utils/dateFormat";

const API = process.env.REACT_APP_BACKEND_URL;

// Mapping statuts → couleurs badge (clés = valeurs normalisées backend)
const STATUT_COLORS = {
  prospection: { bg: "bg-slate-800/30", text: "text-slate-400", border: "border-slate-500/50" },
  en_cours: { bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/50" },
  depose: { bg: "bg-amber-500/20", text: "text-amber-400", border: "border-amber-500/50" },
  instruction: { bg: "bg-[var(--oh-stat-purple-bg)]", text: "text-[var(--oh-stat-purple-text)]", border: "border-[var(--oh-stat-purple-border)]" },
  accorde: { bg: "bg-green-500/20", text: "text-green-400", border: "border-green-500/50" },
  demande_avance: { bg: "bg-[var(--oh-stat-orange-bg)]", text: "text-[var(--oh-stat-orange-text)]", border: "border-[var(--oh-stat-orange-border)]" },
  avance_payee: { bg: "bg-[var(--oh-stat-green-bg)]", text: "text-[var(--oh-stat-green-text)]", border: "border-[var(--oh-stat-green-border)]" },
  demande_solde: { bg: "bg-[var(--oh-stat-cyan-bg)]", text: "text-[var(--oh-stat-cyan-text)]", border: "border-[var(--oh-stat-cyan-border)]" },
  refuse: { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/50" },
  annule: { bg: "bg-slate-800/30", text: "text-slate-400", border: "border-slate-500/50" },
  paye: { bg: "bg-emerald-500/20", text: "text-emerald-400", border: "border-emerald-500/50" },
  cloture: { bg: "bg-slate-800/30", text: "text-slate-400", border: "border-slate-500/50" },
  inconnu: { bg: "bg-[var(--oh-stat-red-bg)]", text: "text-[var(--oh-stat-red-text)]", border: "border-[var(--oh-stat-red-border)]" },
};

const STATUT_LABELS = {
  prospection: "En prospection",
  en_cours: "En cours de montage",
  depose: "Déposé par le demandeur",
  instruction: "En cours d'instruction",
  accorde: "Subvention accordée",
  demande_avance: "Demande d'avance",
  avance_payee: "Avance payée",
  demande_solde: "Demande de solde",
  refuse: "Subvention rejetée",
  annule: "Annulé",
  paye: "Solde payé",
  cloture: "Clôturé",
  inconnu: "Inconnu",
};

// =============================================================================
// PANNEAU CONNEXION ANAH — saisie des identifiants + diagnostic à l'écran
//
// Défini HORS du composant parent : un composant redéfini à chaque rendu
// remonte ses inputs et fait perdre le focus à chaque frappe.
//
// Aucun secret ne transite : le backend ne renvoie que la FORME de
// l'identifiant (aperçu masqué) et un booléen pour le mot de passe.
// =============================================================================

const ETAPE_ICONE = { true: "✓", false: "✗" };

function LigneEtape({ etape }) {
  const ok = etape.ok;
  const famille = ok === true ? "green" : ok === false ? "red" : "amber";
  return (
    <li
      className="flex items-start gap-3 py-2"
      style={{ borderTop: "0.5px solid var(--oh-border)" }}
    >
      <span
        className="shrink-0 inline-flex items-center justify-center"
        style={{
          width: 20, height: 20, borderRadius: 8, fontSize: 12,
          background: `var(--oh-stat-${famille}-bg)`,
          color: `var(--oh-stat-${famille}-text)`,
        }}
        aria-hidden="true"
      >
        {ETAPE_ICONE[String(ok)] || "•"}
      </span>
      <div className="min-w-0 flex-1">
        <div style={{ color: "var(--oh-text)" }}>{etape.etape}</div>
        {etape.detail && (
          <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>{etape.detail}</div>
        )}
        {etape.url && (
          <div
            className="mt-1 break-all"
            style={{ color: "var(--oh-text-muted)", fontSize: 12, fontFamily: "monospace" }}
          >
            {etape.url}
          </div>
        )}
      </div>
    </li>
  );
}

function PanneauConnexionAnah({
  form, identifiant, setIdentifiant, motDePasse, setMotDePasse,
  onEnregistrer, onDiagnostic, enregistrement, diagnostic, resultat,
}) {
  const motDePasseConnu = form?.mot_de_passe_enregistre;
  const dernier = form?.dernier_test;
  const afficher = resultat || dernier;

  const familleResultat = resultat
    ? (resultat.success ? "green" : resultat.motif === "non_soumis" ? "amber" : "red")
    : (dernier?.resultat === "success" ? "green" : "red");

  return (
    <Card style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: 12 }}>
      <CardHeader>
        <CardTitle style={{ color: "var(--oh-text)", fontWeight: 500 }}>Connexion au portail ANAH</CardTitle>
        <CardDescription style={{ color: "var(--oh-text-secondary)" }}>
          Saisissez vos identifiants et testez la connexion. Le diagnostic s'affiche ici, étape par étape.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ═══ #943 · CE QUE LE BACKEND LIT VRAIMENT ══════════════════════
            🔴 JJ lisait « https://acces.anah.fr/ » : le diagnostic affichait
            l'URL passée par `_url_sans_parametres`, qui retire TOUTE la
            requête — donc le `service=`, c'est-à-dire la seule partie qui dit
            où l'on va.
            🔑 Ces valeurs viennent de `resoudre_point_entree()` côté serveur —
            LA résolution, celle qu'emploient aussi le diagnostic, le test
            complet et la synchro réelle. ⛔ Le frontend ne connaît AUCUNE URL
            ANAH : s'il en portait une, elle divergerait de celle utilisée. */}
        {(resultat?.url_utilisee || form?.url_utilisee) && (
          <div className="rounded-lg p-3" data-testid="anah-url-reelle"
               style={{ background: "var(--oh-bg-surface)",
                        border: "0.5px solid var(--oh-border)" }}>
            <div className="flex flex-wrap items-center gap-2">
              <span style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
                URL réellement utilisée
              </span>
              {(resultat?.environnement || form?.environnement) && (
                <span className="px-2 py-0.5 rounded"
                      data-testid="anah-environnement"
                      style={{ fontSize: 12,
                               background: (resultat?.environnement || form?.environnement) === "production"
                                 ? "var(--oh-stat-red-bg)" : "var(--oh-stat-amber-bg)",
                               color: (resultat?.environnement || form?.environnement) === "production"
                                 ? "var(--oh-stat-red-text)" : "var(--oh-stat-amber-text)" }}>
                  {String(resultat?.environnement || form?.environnement).toUpperCase()}
                </span>
              )}
              {(resultat?.point_entree_configure ?? form?.url_configuree_par_operateur) === false && (
                <span className="px-2 py-0.5 rounded" style={{
                  fontSize: 12, background: "var(--oh-stat-amber-bg)",
                  color: "var(--oh-stat-amber-text)" }}>
                  repli — aucune URL configurée
                </span>
              )}
            </div>
            {/* ⚠️ AFFICHÉE TELLE QUELLE : `service=` contient une URL, et il
                ⛔ n'est PAS ré-encodé — l'opérateur doit lire ce qu'il a saisi,
                pas une version échappée. */}
            <div className="mt-1 break-all"
                 style={{ color: "var(--oh-text)", fontSize: 12,
                          fontFamily: "monospace" }}>
              {resultat?.url_utilisee || form?.url_utilisee}
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="anah-identifiant" style={{ color: "var(--oh-text-secondary)", fontWeight: 500 }}>
              Identifiant ANAH
            </Label>
            <Input
              id="anah-identifiant"
              value={identifiant}
              onChange={(e) => setIdentifiant(e.target.value)}
              placeholder={form?.identifiant?.apercu_masque || "adresse e-mail du compte AMO"}
              data-testid="anah-input-identifiant"
            />
            {form?.identifiant?.renseigne && (
              <p style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>
                Enregistré : {form.identifiant.apercu_masque} ({form.identifiant.longueur} caractères)
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="anah-motdepasse" style={{ color: "var(--oh-text-secondary)", fontWeight: 500 }}>
              Mot de passe
            </Label>
            <Input
              id="anah-motdepasse"
              type="password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              placeholder={motDePasseConnu ? "Enregistré — laisser vide pour le conserver" : "mot de passe du compte AMO"}
              data-testid="anah-input-motdepasse"
            />
            <p style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>
              {motDePasseConnu
                ? "Un mot de passe est enregistré. Il ne peut pas être relu, seulement remplacé."
                : "Aucun mot de passe enregistré."}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={onEnregistrer}
            disabled={enregistrement || !identifiant.trim()}
            style={{
              background: "transparent",
              border: "1px solid var(--oh-accent)",
              color: "var(--oh-accent-text)",
            }}
            data-testid="anah-btn-enregistrer"
          >
            {enregistrement ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer les identifiants
          </Button>

          <Button
            onClick={() => onDiagnostic(false)}
            disabled={diagnostic}
            style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}
            data-testid="anah-btn-diagnostic"
          >
            {diagnostic ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Activity className="w-4 h-4 mr-2" />}
            Lancer le diagnostic
          </Button>

          <Button
            onClick={() => onDiagnostic(true)}
            disabled={diagnostic}
            style={{
              background: "transparent",
              border: "1px solid var(--oh-border)",
              color: "var(--oh-text-secondary)",
            }}
            data-testid="anah-btn-test-complet"
          >
            Test complet, avec connexion
          </Button>
        </div>

        <p style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>
          Le diagnostic s'arrête juste avant l'envoi du formulaire : il ne consomme aucune tentative
          de connexion. Le test complet en consomme une.
        </p>

        {form?.champs_sans_effet?.length > 0 && (
          <div
            style={{
              background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)",
              borderRadius: 8, padding: 12, fontSize: 13,
            }}
          >
            Champs sans effet dans Gouvernance &gt; Intégrations : {form.champs_sans_effet.join(", ")}.
            Ils ne sont jamais lus par le connecteur — les URL du portail sont fixées dans le code.
          </div>
        )}

        {afficher && (
          <div className="space-y-3" data-testid="anah-diagnostic-resultat">
            <div
              style={{
                background: `var(--oh-stat-${familleResultat}-bg)`,
                color: `var(--oh-stat-${familleResultat}-text)`,
                borderRadius: 8, padding: 12,
              }}
            >
              {resultat?.message || dernier?.message}
            </div>

            <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>
              {resultat ? (
                <>
                  {resultat.tentative_consommee
                    ? "Une tentative de connexion a été consommée."
                    : "Aucune tentative de connexion consommée."}
                  {resultat.duree_secondes != null && ` · ${resultat.duree_secondes} s`}
                </>
              ) : (
                <>Dernier test : {formatDateFR(dernier?.date)}</>
              )}
            </div>

            {(resultat?.etapes || dernier?.etapes || []).length > 0 && (
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {(resultat?.etapes || dernier?.etapes).map((e, i) => (
                  <LigneEtape key={`${e.etape}-${i}`} etape={e} />
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// =============================================================================
//  🔴 #941 — LE PARCOURS DU RPA, LISIBLE À L'ÉCRAN
// =============================================================================
//  `_trace()` accumulait les étapes côté serveur et ne les persistait JAMAIS
//  dans `anah_sync_logs` : un échec laissait « Échec du login » et rien
//  d'autre. Un échec qu'on ne peut pas LIRE est un échec qu'on ne peut pas
//  CORRIGER — et il fallait mobiliser un agent pour ouvrir un terminal.
//
//  ⚠️ Déclaré HORS du composant parent, délibérément : redéfini à l'intérieur,
//  il serait recréé à chaque rendu et le <details> se replierait tout seul
//  (même classe de bug que `MoneyInput` dans `FinancementTab`).
//  ⚠️ Couleurs en tokens `var(--oh-*)` : lisible dans les DEUX modes.
// =============================================================================
const ACTION_COULEUR = {
  navigation: "blue", clic: "purple", saisie: "cyan",
  attente: "amber", soumission: "green", autre: "slate",
};

function ParcoursRPA({ etapes }) {
  if (!etapes || etapes.length === 0) {
    // ⚠️ Une absence de parcours se DIT. Un bloc vide se lirait comme
    // « tout s'est bien passé », alors qu'il signifie « on ne sait pas ».
    return (
      <p className="mt-2 text-sm" style={{ color: "var(--oh-text-muted)" }}
         data-testid="parcours-vide">
        Aucun parcours enregistré pour cette synchronisation (antérieure au
        journal, ou interrompue avant la première étape).
      </p>
    );
  }

  return (
    <details className="mt-3" data-testid="parcours-details">
      <summary
        className="cursor-pointer text-sm select-none"
        style={{ color: "var(--oh-accent-text)" }}
        data-testid="parcours-toggle"
      >
        Voir le parcours du robot ({etapes.length} étape{etapes.length > 1 ? "s" : ""})
      </summary>

      <div className="mt-2 overflow-x-auto">
        <ol className="space-y-2 min-w-[520px]">
          {etapes.map((e, i) => {
            const couleur = ACTION_COULEUR[e.action] || "slate";
            const verdict = e.ok === true ? "green" : e.ok === false ? "red" : "slate";
            return (
              <li
                key={e.ordre ?? i}
                className="rounded-lg p-3 text-sm"
                style={{
                  background: "var(--oh-bg-elevated)",
                  border: "0.5px solid var(--oh-border)",
                }}
                data-testid={`parcours-etape-${e.ordre ?? i + 1}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span style={{ color: "var(--oh-text-muted)" }}>#{e.ordre ?? i + 1}</span>
                  <span
                    className="px-2 py-0.5 rounded"
                    style={{
                      background: `var(--oh-stat-${couleur}-bg)`,
                      color: `var(--oh-stat-${couleur}-text)`,
                    }}
                  >
                    {e.action || "autre"}
                  </span>
                  <span style={{ color: "var(--oh-text)" }}>{e.etape}</span>
                  <span
                    className="px-2 py-0.5 rounded"
                    style={{
                      background: `var(--oh-stat-${verdict}-bg)`,
                      color: `var(--oh-stat-${verdict}-text)`,
                    }}
                  >
                    {e.ok === true ? "ok" : e.ok === false ? "ko" : "—"}
                  </span>
                  {e.duree_ms > 0 && (
                    <span style={{ color: "var(--oh-text-muted)" }}>{e.duree_ms} ms</span>
                  )}
                  {e.horodatage && (
                    <span style={{ color: "var(--oh-text-muted)" }}>
                      {String(e.horodatage).slice(11, 19)}
                    </span>
                  )}
                </div>

                {(e.url || e.titre) && (
                  <div className="mt-1" style={{ color: "var(--oh-text-secondary)" }}>
                    {/* ⚠️ URL déjà assainie côté serveur : jamais de jeton ici. */}
                    {e.url && <span className="break-all">{e.url}</span>}
                    {e.titre && <span> — « {e.titre} »</span>}
                  </div>
                )}

                {e.detail && (
                  <div className="mt-1" style={{ color: "var(--oh-text-secondary)" }}>
                    {e.detail}
                  </div>
                )}

                {e.selecteurs?.length > 0 && (
                  <ul className="mt-1 space-y-0.5" style={{ color: "var(--oh-text-muted)" }}>
                    {e.selecteurs.map((sel, j) => (
                      <li key={j} className="break-all">
                        <code>{sel.selecteur}</code>{" "}
                        {/* 🔑 « trouvé » et « visible » sont DEUX faits distincts :
                            sur une page à onglets, six formulaires coexistent dans
                            le DOM et un seul est affiché. */}
                        <span style={{
                          color: sel.trouve
                            ? "var(--oh-stat-green-text)"
                            : "var(--oh-stat-red-text)",
                        }}>
                          {sel.trouve ? "trouvé" : "absent du DOM"}
                        </span>
                        {" · "}
                        <span style={{
                          color: sel.visible
                            ? "var(--oh-stat-green-text)"
                            : "var(--oh-stat-amber-text)",
                        }}>
                          {sel.visible ? "visible" : "non visible"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {e.erreur_portail && (
                  <div
                    className="mt-2 rounded p-2"
                    style={{
                      background: "var(--oh-stat-red-bg)",
                      color: "var(--oh-stat-red-text)",
                    }}
                    data-testid="parcours-erreur-portail"
                  >
                    {/* 🔑 Le mot du PORTAIL, pas le nôtre : « identifiants
                        incorrects » et « compte verrouillé » n'appellent pas la
                        même action. */}
                    Message du portail : {e.erreur_portail}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </details>
  );
}

export default function AnahSyncPage({ tenantId }) {
  // ─── State chargements / synchronisation ───
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncingOH, setSyncingOH] = useState(false);
  const [syncOHResult, setSyncOHResult] = useState(null);
  // BUG-SYNC-ANAH-CHAMPS (OH-0012) — arbitrage des divergences (cas 4)
  const [arbChoices, setArbChoices] = useState({}); // index -> "oh" | "anah"
  const [applyingArb, setApplyingArb] = useState(false);

  // ─── State connexion ANAH (saisie + diagnostic à l'écran) ───
  const [credForm, setCredForm] = useState(null);
  const [identifiant, setIdentifiant] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [enregistrement, setEnregistrement] = useState(false);
  const [diagnostic, setDiagnostic] = useState(false);
  const [diagResultat, setDiagResultat] = useState(null);

  // ─── State données ───
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [stats, setStats] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  // ─── #942 · import manuel d'un export ANAH ───
  const [importEnCours, setImportEnCours] = useState(false);
  const [rapportImport, setRapportImport] = useState(null);
  const [fichierChoisi, setFichierChoisi] = useState(null);
  const [exportsConserves, setExportsConserves] = useState([]);
  // ⚠️ Périmètre témoin par défaut : 5. ⛔ PAS « tout » — un premier import
  // écrirait 265 dossiers d'un coup.
  const [limiteImport, setLimiteImport] = useState(5);
  const champFichier = useRef(null);

  // ─── Logs d'activité live ───
  const [activityLogs, setActivityLogs] = useState([]);
  const [showActivityPanel, setShowActivityPanel] = useState(false);

  // ─── Modal détail ───
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [creatingDossier, setCreatingDossier] = useState(false);
  const [linkedDossierOH, setLinkedDossierOH] = useState(null);

  // ─── Filtres avancés (via OHGrid) ───
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterAide, setFilterAide] = useState("all");

  // ─── Helpers ───
  const addActivityLog = useCallback((type, message, details = null) => {
    setActivityLogs(prev => [...prev, { timestamp: new Date().toISOString(), type, message, details }]);
  }, []);

  const clearActivityLogs = useCallback(() => setActivityLogs([]), []);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('session_token');
    const userId = localStorage.getItem('user_id');
    const userRole = localStorage.getItem('user_role');
    return {
      "X-Tenant-ID": tenantId || "tef",
      "Content-Type": "application/json",
      ...(userId && { "X-User-ID": userId }),
      ...(userRole && { "X-User-Role": userRole }),
      ...(token && { "Authorization": `Bearer ${token}` }),
    };
  }, [tenantId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return "Jamais";
    // Défensif : si le backend envoie une date ISO SANS timezone,
    // JS interprète en heure locale (bug timezone → décalage de 2h à Paris).
    // On force l'interprétation UTC quand aucun suffixe n'est présent.
    let safeStr = dateStr;
    if (typeof dateStr === "string" && dateStr) {
      const last6 = dateStr.slice(-6);
      const hasTz = dateStr.endsWith("Z") || dateStr.endsWith("z")
        || last6.includes("+") || last6.slice(1).includes("-");
      if (!hasTz) safeStr = `${dateStr}Z`;
    }
    const diffMs = new Date() - new Date(safeStr);
    if (Number.isNaN(diffMs)) return "-";
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    return `Il y a ${diffDays}j`;
  };

  // ─── Loaders (useCallback AVANT useEffect — règle anti-TDZ) ───
  const loadConnectionStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/anah/connection-status`, { headers: getAuthHeaders() });
      if (res.ok) setConnectionStatus(await res.json());
    } catch (e) { console.error("Erreur statut connexion:", e); }
  }, [getAuthHeaders]);

  const loadDossiers = useCallback(async () => {
    try {
      // Chargement complet (OHGrid gère tri/filtres/pagination côté client)
      const res = await fetch(`${API}/api/anah/dossiers?limit=2000`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        // Ajout d'un `id` stable pour OHGrid (basé sur reference_anah)
        const enriched = (data.dossiers || []).map(d => ({ ...d, id: d.reference_anah || d._id }));
        setDossiers(enriched);
      }
    } catch (e) { console.error("Erreur chargement dossiers:", e); }
  }, [getAuthHeaders]);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/anah/stats`, { headers: getAuthHeaders() });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error("Erreur stats:", e); }
  }, [getAuthHeaders]);

  const loadSyncLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/anah/sync-logs?limit=10`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSyncLogs(data.logs || []);
      }
    } catch (e) { console.error("Erreur logs:", e); }
  }, [getAuthHeaders]);

  const checkLinkedDossier = useCallback(async (referenceAnah) => {
    try {
      const res = await fetch(`${API}/api/metier/dossiers/by-anah-ref/${referenceAnah}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data.found && data.dossier) {
          setLinkedDossierOH(data.dossier);
          return data.dossier;
        }
      }
      setLinkedDossierOH(null);
      return null;
    } catch (e) {
      console.error("Erreur vérification liaison:", e);
      setLinkedDossierOH(null);
      return null;
    }
  }, [getAuthHeaders]);

  const openDetail = useCallback(async (dossier) => {
    setSelectedDossier(dossier);
    setShowDetailModal(true);
    setIsEditing(false);
    await checkLinkedDossier(dossier.reference_anah);
  }, [checkLinkedDossier]);

  // ─── Polling statut de synchronisation ───
  const pollSyncStatus = useCallback(async () => {
    const maxAttempts = 60;
    let attempts = 0;
    while (attempts < maxAttempts) {
      attempts++;
      await new Promise(r => setTimeout(r, 5000));
      try {
        const res = await fetch(`${API}/api/anah/sync-status`, { headers: getAuthHeaders() });
        const data = await res.json();
        if (data.status === 'in_progress') {
          if (attempts % 4 === 0) addActivityLog('info', `Synchronisation en cours... (${attempts * 5}s)`);
          continue;
        }
        if (data.status === 'completed') {
          if (data.success) {
            addActivityLog('success', data.message || 'Synchronisation terminée');
            addActivityLog('info', `Dossiers trouvés: ${data.dossiers_found || 0}`);
            addActivityLog('info', `Dossiers synchronisés: ${data.dossiers_synced || 0}`);
            (data.errors || []).forEach(err => addActivityLog('warning', err));
            toast.success(`Synchronisation terminée: ${data.dossiers_synced} dossiers`);
            await Promise.all([loadDossiers(), loadStats(), loadSyncLogs(), loadConnectionStatus()]);
          } else {
            addActivityLog('error', `Échec: ${data.message}`);
            (data.errors || []).forEach(err => addActivityLog('error', `→ ${err}`));
            toast.error(data.message || "Échec de la synchronisation");
          }
          break;
        }
        if (data.status === 'idle') { addActivityLog('info', 'Synchronisation terminée (aucun résultat)'); break; }
      } catch (e) { addActivityLog('warning', `Erreur vérification statut: ${e.message}`); }
    }
    if (attempts >= maxAttempts) {
      addActivityLog('error', 'Timeout de synchronisation');
      toast.error("La synchronisation a pris trop de temps");
    }
    setSyncing(false);
  }, [getAuthHeaders, addActivityLog, loadDossiers, loadStats, loadSyncLogs, loadConnectionStatus]);

  // ─── Connexion ANAH : saisie des identifiants + diagnostic ───
  const loadCredForm = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/anah/credentials-form`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setCredForm(data);
      if (data?.identifiant?.renseigne && !identifiant) setIdentifiant("");
    } catch { /* panneau simplement non renseigné */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders]);

  const chargerExportsConserves = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/anah/exports-conserves`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setExportsConserves(data.exports || []);
    } catch { /* liste simplement indisponible */ }
  }, [getAuthHeaders]);

  // ─── Chargement initial ───
  // 🔴 #941 — CE `useEffect` ÉTAIT DÉCLARÉ LIGNE 422, soit AVANT
  // `loadCredForm` (ligne 472). Son tableau de dépendances est évalué À CHAQUE
  // RENDU : il lisait donc un `const` encore dans sa zone morte temporelle
  // (TDZ) ⇒ `ReferenceError: Cannot access 'loadCredForm' before
  // initialization`, et l'écran ANAH ne montait pas du tout — d'où le rollback.
  // ⚠️ Un effet doit être déclaré APRÈS TOUTES les fonctions qu'il référence.
  // ⛔ Ce n'est pas une question de style : `const`/`let` ne sont pas hissés
  // comme `function`, et une dépendance manquante ne pardonne pas.
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadConnectionStatus(), loadDossiers(), loadStats(),
                         loadSyncLogs(), loadCredForm(), chargerExportsConserves()]);
      setLoading(false);
    };
    loadAll();
  }, [loadConnectionStatus, loadDossiers, loadStats, loadSyncLogs, loadCredForm,
      chargerExportsConserves]);

  // ─── #942 · l'import : APERÇU d'abord, écriture ensuite ───
  // 🔑 `appliquer` est un PARAMÈTRE, ⛔ pas deux fonctions : le même appel, la
  // même chaîne côté serveur. Deux fonctions divergeraient — c'est le défaut
  // qu'on combat depuis #926.
  const envoyerFichier = useCallback(async (fichier, appliquer) => {
    if (!fichier) return;
    setImportEnCours(true);
    try {
      const corps = new FormData();
      corps.append("fichier", fichier);
      const params = new URLSearchParams({ appliquer: String(appliquer) });
      if (limiteImport) params.set("limite", String(limiteImport));
      const res = await fetch(`${API}/api/anah/importer-export?${params}`, {
        method: "POST", headers: getAuthHeaders(), body: corps,
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || `Erreur HTTP ${res.status}`); return; }
      setRapportImport({ ...data, fichierNom: fichier.name });
      if (!data.verdict_fichier?.accepte) {
        toast.error("Fichier refusé — ce n'est pas un export ANAH");
      } else if (!data.complete) {
        toast.error("Fichier incomplet — aucune écriture");
      } else if (appliquer) {
        toast.success(`${data.dossiers_ecrits} dossier(s) écrit(s)`);
        await Promise.all([loadDossiers(), loadStats(), loadSyncLogs()]);
      } else {
        toast.success(`Aperçu : ${data.dossiers_examines} dossier(s) projeté(s)`);
      }
      await chargerExportsConserves();
    } catch (e) {
      toast.error(`Import impossible : ${e.message}`);
    } finally {
      setImportEnCours(false);
    }
  }, [getAuthHeaders, limiteImport, chargerExportsConserves, loadDossiers,
      loadStats, loadSyncLogs]);

  const enregistrerIdentifiants = async () => {
    setEnregistrement(true);
    try {
      const corps = { username: identifiant.trim() };
      if (motDePasse) corps.password = motDePasse;
      const res = await fetch(`${API}/api/anah/credentials-form`, {
        method: "PUT",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(corps),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || "Identifiants enregistrés");
        setMotDePasse("");
        await loadCredForm();
      } else {
        toast.error(data.detail || data.message || `Erreur HTTP ${res.status}`);
      }
    } catch (e) {
      toast.error(`Erreur réseau : ${e.message}`);
    } finally {
      setEnregistrement(false);
    }
  };

  const lancerDiagnostic = async (soumettre) => {
    setDiagnostic(true);
    setDiagResultat(null);
    try {
      const res = await fetch(`${API}/api/anah/diagnose?soumettre=${soumettre ? "true" : "false"}`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) {
        setDiagResultat({ success: false, motif: "erreur_technique", etapes: [],
          message: data.detail || `Erreur HTTP ${res.status}` });
        return;
      }
      setDiagResultat(data);
      // Le message porte le diagnostic : on ne le double pas d'un toast accusateur.
      if (data.success) toast.success("Connexion ANAH réussie");
      await loadCredForm();
    } catch (e) {
      setDiagResultat({ success: false, motif: "erreur_technique", etapes: [],
        message: `Erreur réseau : ${e.message}` });
    } finally {
      setDiagnostic(false);
    }
  };

  // ─── Actions synchronisation ───
  const testConnection = async () => {
    setTesting(true);
    setShowActivityPanel(true);
    addActivityLog('info', 'Démarrage du test de connexion ANAH...');
    try {
      const res = await fetch(`${API}/api/anah/test-connection`, { method: 'POST', headers: getAuthHeaders() });
      const responseText = await res.text();
      let data; try { data = JSON.parse(responseText); } catch { data = { success: false, message: responseText }; }
      if (res.ok && data.success) {
        addActivityLog('success', 'Connexion ANAH réussie', data);
        toast.success("Connexion ANAH réussie");
      } else {
        const msg = data.detail || data.message || `Erreur HTTP ${res.status}`;
        addActivityLog('error', `Échec: ${msg}`);
        toast.error(msg);
      }
    } catch (e) { addActivityLog('error', `Erreur réseau: ${e.message}`); toast.error("Erreur test connexion"); }
    finally { setTesting(false); }
  };

  const runSyncExport = async () => {
    setSyncing(true);
    setShowActivityPanel(true);
    clearActivityLogs();
    addActivityLog('info', 'Synchronisation via Export ANAH...');
    try {
      const res = await fetch(`${API}/api/anah/sync-export`, { method: 'POST', headers: getAuthHeaders() });
      const responseText = await res.text();
      let data; try { data = JSON.parse(responseText); } catch { data = { success: false, message: responseText }; }
      if (!res.ok || (!data.success && data.status !== 'started')) {
        const msg = data.detail || data.message || `Erreur HTTP ${res.status}`;
        addActivityLog('error', `Échec: ${msg}`);
        toast.error(msg);
        setSyncing(false);
        return;
      }
      if (data.status === 'in_progress') addActivityLog('warning', 'Une synchronisation est déjà en cours');
      else addActivityLog('success', 'Export démarré en arrière-plan');
      addActivityLog('info', 'Attente du téléchargement et parsing...');
      await pollSyncStatus();
    } catch (e) {
      addActivityLog('error', `Erreur réseau: ${e.message}`);
      toast.error("Erreur synchronisation");
      setSyncing(false);
    }
  };

  const runSyncToOH = async () => {
    setSyncingOH(true);
    setSyncOHResult(null);
    setShowActivityPanel(true);
    addActivityLog('info', 'Démarrage synchronisation ANAH → OmniHub...');
    try {
      const res = await fetch(`${API}/api/anah/sync-to-oh`, { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      if (res.ok && data.success) {
        setSyncOHResult(data);
        addActivityLog('success', `Terminé: ${data.message}`);
        if (data.created > 0) {
          toast.success(`${data.created} dossier(s) OH créé(s)`);
          addActivityLog('info', `Nouveaux: ${data.created_list?.map(d => d.référence).join(', ')}`);
        }
        if (data.updated_statuts > 0) addActivityLog('info', `${data.updated_statuts} statut(s) mis à jour`);
        (data.errors || []).forEach(e => addActivityLog('warning', `Erreur: ${e}`));
        if (!data.created && !data.updated_statuts) toast.info('Tout est déjà synchronisé');
        await Promise.all([loadDossiers(), loadStats()]);
      } else {
        const msg = data.detail || data.message || 'Erreur inconnue';
        addActivityLog('error', `Échec: ${msg}`);
        toast.error(msg);
      }
    } catch (e) { addActivityLog('error', `Erreur réseau: ${e.message}`); toast.error('Erreur réseau'); }
    finally { setSyncingOH(false); }
  };

  // BUG-SYNC-ANAH-CHAMPS (OH-0012) — applique les choix d'arbitrage (cas 4)
  const applyArbitrage = async () => {
    const divs = syncOHResult?.divergences || [];
    const decisions = divs
      .map((d, i) => ({ d, choice: arbChoices[i] }))
      .filter(x => x.choice) // seulement les lignes arbitrées
      .map(({ d, choice }) => ({
        target: d.target, dossier_id: d.dossier_id, client_id: d.client_id,
        path: d.path, value: d.anah, choice,
      }));
    if (decisions.length === 0) { toast.info('Aucun choix sélectionné'); return; }
    setApplyingArb(true);
    try {
      const res = await fetch(`${API}/api/anah/apply-arbitrage`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ decisions }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        addActivityLog('success', `Arbitrage: ${data.message}`);
        // retirer les lignes arbitrées de la liste
        const resolved = new Set(Object.keys(arbChoices).map(Number));
        setSyncOHResult(prev => ({
          ...prev,
          divergences: (prev.divergences || []).filter((_, i) => !resolved.has(i)),
          divergences_count: (prev.divergences || []).filter((_, i) => !resolved.has(i)).length,
        }));
        setArbChoices({});
        await Promise.all([loadDossiers(), loadStats()]);
      } else {
        toast.error(data.detail || data.message || 'Erreur arbitrage');
      }
    } catch (e) { toast.error('Erreur réseau arbitrage'); }
    finally { setApplyingArb(false); }
  };

  // ─── Actions dossier ───
  const createDossierFromAnah = async () => {
    if (!selectedDossier) return;
    setCreatingDossier(true);
    try {
      const res = await fetch(`${API}/api/anah/create-dossier-oh`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ reference_anah: selectedDossier.reference_anah, workflow_type: "dossier_amo" }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Dossier OH créé: ${data.dossier.référence}`);
        setLinkedDossierOH(data.dossier);
      } else toast.error(data.detail || "Erreur lors de la création");
    } catch (e) { console.error(e); toast.error("Erreur réseau"); }
    finally { setCreatingDossier(false); }
  };

  const createBulkDossiersOH = async (ids) => {
    if (!ids.length) { toast.warning("Aucun dossier sélectionné"); return; }
    toast.info(`Création de ${ids.length} dossiers OH...`);
    let created = 0, errors = 0;
    for (const refAnah of ids) {
      try {
        const res = await fetch(`${API}/api/anah/create-dossier-oh`, {
          method: 'POST', headers: getAuthHeaders(),
          body: JSON.stringify({ reference_anah: refAnah, workflow_type: "dossier_amo" }),
        });
        const data = await res.json();
        if (res.ok && data.success) created++;
        else if (data.detail && (data.detail.includes("déjà lié") || data.detail.includes("déjà lie") || data.detail.includes("existe"))) created++;
        else { errors++; console.warn(`Erreur ${refAnah}:`, data.detail); }
      } catch (e) { errors++; console.error(`Erreur réseau ${refAnah}:`, e); }
    }
    if (created > 0) toast.success(`${created} dossier(s) OH créé(s)`);
    if (errors > 0) toast.warning(`${errors} erreur(s)`);
    loadDossiers();
  };

  const saveManualData = async () => {
    if (!selectedDossier) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/anah/dossiers/${selectedDossier.reference_anah}/manual-data`, {
        method: 'PATCH', headers: getAuthHeaders(),
        body: JSON.stringify({
          subvention_manuelle: editData.subvention_accordee || null,
          telephone: editData.contact_telephone || null,
          notes: editData.notes || null,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Informations sauvegardées");
        setSelectedDossier(prev => ({
          ...prev,
          manual_data: {
            ...prev.manual_data,
            subvention_manuelle: editData.subvention_accordee,
            telephone: editData.contact_telephone,
            notes: editData.notes,
          },
        }));
        setIsEditing(false);
        loadDossiers();
      } else toast.error(data.detail || "Erreur sauvegarde");
    } catch { toast.error("Erreur réseau"); }
    finally { setSaving(false); }
  };

  // ─── Config OHGrid ───
  const ohStats = useMemo(() => [
    { key: "total", label: "Total dossiers", value: stats?.total ?? dossiers.length, icon: FileText, color: "cyan", filter: null },
    { key: "accorde", label: "Accordés", value: stats?.par_statut?.accorde ?? 0, icon: CheckCircle, color: "green",
      filter: (row) => row.statut_normalise === "accorde" },
    { key: "instruction", label: "En instruction", value: stats?.par_statut?.instruction ?? 0, icon: Clock, color: "purple",
      filter: (row) => row.statut_normalise === "instruction" },
    { key: "last_sync", label: "Dernière sync", value: formatRelativeTime(connectionStatus?.last_sync), icon: Calendar, color: "gray", filter: null },
  ], [stats, dossiers.length, connectionStatus?.last_sync]);

  const advancedFilters = useMemo(() => [
    {
      key: "statut", label: "Statut", type: "select", defaultValue: "all",
      options: Object.entries(STATUT_LABELS).map(([v, l]) => ({ value: v, label: l })),
    },
    {
      key: "aide", label: "Dispositif", type: "select", defaultValue: "all",
      options: [
        { value: "MaPrimeAdapt", label: "MaPrimeAdapt'" },
        { value: "Autonomie", label: "Autonomie" },
        { value: "Énergie", label: "Énergie" },
        { value: "Ravalement", label: "Ravalement" },
        { value: "Logement Décent", label: "Logement Décent" },
      ],
    },
  ], []);

  const handleAdvFilter = useCallback(({ key, value }) => {
    if (key === "statut") setFilterStatut(value);
    if (key === "aide") setFilterAide(value);
  }, []);

  const filteredDossiers = useMemo(() => dossiers.filter(d => {
    if (filterStatut !== "all" && d.statut_normalise !== filterStatut) return false;
    if (filterAide !== "all") {
      const aide = (d.aide_type || "").toString();
      if (!aide.toLowerCase().includes(filterAide.toLowerCase())) return false;
    }
    return true;
  }), [dossiers, filterStatut, filterAide]);

  const searchFilter = useCallback((row, q) => (
    (row.reference_anah || "").toLowerCase().includes(q) ||
    (row.reference_opal || "").toLowerCase().includes(q) ||
    (row.nom_client || "").toLowerCase().includes(q) ||
    (row.adresse || "").toLowerCase().includes(q) ||
    (row.mandataire || "").toLowerCase().includes(q)
  ), []);

  const columns = useMemo(() => [
    { key: "reference_anah", label: "Réf. ANAH",
      render: (v) => <span className="font-mono font-medium">{v || "—"}</span> },
    { key: "reference_opal", label: "Réf. OPAL",
      render: (v) => <span className="font-mono text-xs" style={{ color: "var(--oh-text-muted)" }}>{v || "—"}</span> },
    { key: "nom_client", label: "Demandeur",
      render: (v) => <span className="text-cyan-400 font-medium">{v || "—"}</span> },
    { key: "adresse", label: "Adresse", sortable: false,
      render: (v) => v ? <span className="text-xs truncate block max-w-[200px]" title={v} style={{ color: "var(--oh-text-muted)" }}>{v}</span> : "—" },
    { key: "statut_normalise", label: "Statut",
      render: (v) => {
        const colors = STATUT_COLORS[v] || STATUT_COLORS.inconnu;
        return <Badge className={`${colors.bg} ${colors.text} ${colors.border} border text-xs`}>{STATUT_LABELS[v] || v || "—"}</Badge>;
      }},
    { key: "aide_type", label: "Dispositif",
      render: (v) => <span className="text-xs">{v || "—"}</span> },
    { key: "mandataire", label: "Mandataire",
      render: (v) => <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{v || "—"}</span> },
    { key: "date_depot", label: "Date dépôt",
      render: (v) => <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{v ? formatDateFR(v) : "—"}</span> },
  ], []);

  const rowActions = useMemo(() => [
    { label: "Voir la fiche", icon: Eye, onClick: (row) => openDetail(row) },
    { label: "Créer dossier OH", icon: FolderPlus,
      onClick: async (row) => {
        const linked = await checkLinkedDossier(row.reference_anah);
        if (linked) {
          toast.info(`Dossier OH déjà lié: ${linked.référence || linked.id}`);
          return;
        }
        await createBulkDossiersOH([row.reference_anah]);
      }},
  ], [openDetail, checkLinkedDossier]);

  const bulkActions = useMemo(() => [
    { label: "Créer dossiers OH", icon: FolderPlus, onClick: createBulkDossiersOH,
      className: "border-[var(--oh-stat-green-border)] text-[var(--oh-stat-green-text)] hover:bg-[var(--oh-stat-green-bg)]" },
  ], []);

  // ─── Render ───
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="anah-sync-loading">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
        <span className="ml-3 text-slate-400">Chargement module ANAH...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="anah-sync-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Building2 className="w-7 h-7 text-teal-400" />
            Aides ANAH
          </h1>
          <p className="text-slate-400 mt-1">
            Consultation des dossiers ANAH synchronisés (module isolé)
          </p>
        </div>
        <div className="flex items-center gap-3">
          {connectionStatus?.configured && (
            <>
              <Button variant="outline" onClick={testConnection} disabled={testing}
                className="border-slate-600 text-slate-300" data-testid="btn-test-connection">
                {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Activity className="w-4 h-4 mr-2" />}
                Tester
              </Button>
              <Button onClick={runSyncExport} disabled={syncing}
                className="bg-teal-600 hover:bg-teal-700" data-testid="btn-sync-anah">
                {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Sync ANAH
              </Button>
              <Button onClick={runSyncToOH} disabled={syncingOH || syncing}
                className="bg-orange-600 hover:bg-orange-700" data-testid="btn-sync-oh">
                {syncingOH ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FolderPlus className="w-4 h-4 mr-2" />}
                Sync OH
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Connexion ANAH — saisie des identifiants + diagnostic à l'écran.
          Placé haut : c'est le premier écran utile quand la synchro échoue,
          et il évite d'aller jusqu'à Gouvernance > Intégrations. */}
      <PanneauConnexionAnah
        form={credForm}
        identifiant={identifiant}
        setIdentifiant={setIdentifiant}
        motDePasse={motDePasse}
        setMotDePasse={setMotDePasse}
        onEnregistrer={enregistrerIdentifiants}
        onDiagnostic={lancerDiagnostic}
        enregistrement={enregistrement}
        diagnostic={diagnostic}
        resultat={diagResultat}
      />

      {/* Résultat Sync OH */}
      {syncOHResult && (
        <Card className="bg-slate-900 border-teal-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-teal-400 font-medium flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Résultat synchronisation OH
              </h3>
              <Button size="sm" variant="ghost" onClick={() => setSyncOHResult(null)} className="text-slate-400 h-7 px-2">Fermer</Button>
            </div>
            {/* Compteur HONNÊTE — écritures métier réelles (OH-0012) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 text-center">
              <div className="bg-green-500/10 rounded p-2">
                <p className="text-2xl font-bold text-green-400">{syncOHResult.created}</p>
                <p className="text-xs text-slate-400">Créés</p>
              </div>
              <div className="bg-emerald-500/10 rounded p-2">
                <p className="text-2xl font-bold text-emerald-400">{syncOHResult.enriched_fields ?? 0}</p>
                <p className="text-xs text-slate-400">Champs enrichis</p>
              </div>
              <div className="bg-slate-700/50 rounded p-2">
                <p className="text-2xl font-bold text-slate-300">{syncOHResult.identical_fields ?? 0}</p>
                <p className="text-xs text-slate-400">Identiques</p>
              </div>
              <div className="bg-amber-500/10 rounded p-2">
                <p className="text-2xl font-bold text-amber-400">{syncOHResult.divergences_count ?? 0}</p>
                <p className="text-xs text-slate-400">Divergences</p>
              </div>
              <div className="bg-blue-500/10 rounded p-2">
                <p className="text-2xl font-bold text-blue-400">{syncOHResult.updated_statuts}</p>
                <p className="text-xs text-slate-400">Statuts MàJ</p>
              </div>
              <div className="bg-red-500/10 rounded p-2">
                <p className="text-2xl font-bold text-red-400">{syncOHResult.errors?.length || 0}</p>
                <p className="text-xs text-slate-400">Erreurs</p>
              </div>
            </div>
            {syncOHResult.created_list?.length > 0 && (
              <div className="mt-3 text-xs text-slate-400 max-h-24 overflow-y-auto">
                <p className="text-slate-300 font-medium mb-1">Nouveaux dossiers :</p>
                {syncOHResult.created_list.map((d, i) => (
                  <span key={i} className="inline-block bg-slate-800 rounded px-2 py-0.5 mr-1 mb-1">
                    {d.référence} ({d.nom_client})
                  </span>
                ))}
              </div>
            )}

            {/* Garde-fou matching : matchs ambigus signalés (non traités) */}
            {syncOHResult.doubtful_matches?.length > 0 && (
              <div className="mt-3 text-xs bg-orange-500/10 border border-orange-500/30 rounded p-2">
                <p className="text-orange-300 font-medium mb-1">
                  ⚠ {syncOHResult.doubtful_matches.length} match(s) ambigu(s) signalé(s) (non traités) :
                </p>
                {syncOHResult.doubtful_matches.map((x, i) => (
                  <div key={i} className="text-slate-400">
                    ANAH {x.reference_anah} · {x.nom_client} → {x.candidats?.length} candidats proches
                  </div>
                ))}
              </div>
            )}

            {/* Temps B — divergences à arbitrer (cas 4) : Garder OH / Prendre ANAH */}
            {syncOHResult.divergences?.length > 0 && (
              <div className="mt-4" data-testid="anah-divergences">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-amber-400 font-medium text-sm">
                    Divergences à arbitrer ({syncOHResult.divergences.length}) — ne JAMAIS écraser sans votre choix
                  </h4>
                  <Button size="sm" onClick={applyArbitrage} disabled={applyingArb}
                    className="bg-teal-600 hover:bg-teal-700 h-7" data-testid="apply-arbitrage">
                    {applyingArb ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                    Valider l'arbitrage
                  </Button>
                </div>
                <div className="max-h-80 overflow-auto border border-slate-700 rounded">
                  <table className="w-full text-xs min-w-[720px]">
                    <thead className="bg-slate-800 text-slate-400 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1.5">Dossier</th>
                        <th className="text-left px-2 py-1.5">Champ</th>
                        <th className="text-left px-2 py-1.5">Valeur OH</th>
                        <th className="text-left px-2 py-1.5">Valeur ANAH</th>
                        <th className="text-center px-2 py-1.5">Choix</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncOHResult.divergences.map((d, i) => (
                        <tr key={i} className="border-t border-slate-800" data-testid={`divergence-${i}`}>
                          <td className="px-2 py-1.5 text-slate-300">{d.dossier_reference || d.reference_anah}</td>
                          <td className="px-2 py-1.5 text-slate-300">{d.label}</td>
                          <td className="px-2 py-1.5 text-blue-300 font-mono">{String(d.oh ?? '')}</td>
                          <td className="px-2 py-1.5 text-emerald-300 font-mono">{String(d.anah ?? '')}</td>
                          <td className="px-2 py-1.5">
                            <div className="flex gap-1 justify-center">
                              <button
                                onClick={() => setArbChoices(p => ({ ...p, [i]: 'oh' }))}
                                className={`px-2 py-0.5 rounded text-[11px] border ${arbChoices[i] === 'oh' ? 'bg-blue-600 text-white border-blue-500' : 'border-slate-600 text-slate-400 hover:text-white'}`}
                                data-testid={`keep-oh-${i}`}
                              >Garder OH</button>
                              <button
                                onClick={() => setArbChoices(p => ({ ...p, [i]: 'anah' }))}
                                className={`px-2 py-0.5 rounded text-[11px] border ${arbChoices[i] === 'anah' ? 'bg-emerald-600 text-white border-emerald-500' : 'border-slate-600 text-slate-400 hover:text-white'}`}
                                data-testid={`take-anah-${i}`}
                              >Prendre ANAH</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Logs d'activité live */}
      {showActivityPanel && activityLogs.length > 0 && (
        <Card className="bg-slate-900 border-slate-700">
          <CardHeader className="pb-2 flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Activity className="w-4 h-4 text-teal-400" />
              Logs d'activité
              {(syncing || testing) && <Loader2 className="w-3 h-3 animate-spin text-teal-400 ml-2" />}
            </CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={clearActivityLogs} className="text-slate-400 hover:text-white h-7 px-2">Effacer</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowActivityPanel(false)} className="text-slate-400 hover:text-white h-7 px-2">Masquer</Button>
            </div>
          </CardHeader>
          <CardContent className="p-3">
            <div className="bg-slate-950 rounded-lg p-3 max-h-48 overflow-y-auto font-mono text-xs space-y-1">
              {activityLogs.map((log, idx) => (
                <div key={idx}
                  className={`flex items-start gap-2 ${
                    log.type === 'error' ? 'text-red-400' :
                    log.type === 'success' ? 'text-green-400' :
                    log.type === 'warning' ? 'text-amber-400' :
                    'text-slate-400'
                  }`}>
                  <span className="text-zinc-400 flex-shrink-0">{new Date(log.timestamp).toLocaleTimeString('fr-FR')}</span>
                  <span>{log.message}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statut connexion / contenu principal */}
      {connectionStatus?.enabled === false ? (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <XCircle className="w-8 h-8 text-red-400 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-red-300">Module non disponible pour ce tenant</h3>
                <p className="text-red-200/70 mt-1">Le module ANAH Sync est réservé au tenant TEF uniquement.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : !connectionStatus?.configured ? (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-amber-400 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-amber-300">Connexion ANAH non configurée</h3>
                <p className="text-amber-200/70 mt-1">Pour utiliser ce module, configurez d'abord vos identifiants ANAH dans :</p>
                <p className="text-amber-300 font-medium mt-2">Hub Gouvernance → Intégrations → ANAH AMO</p>
                <Button variant="outline" className="mt-4 border-amber-500/50 text-amber-300 hover:bg-amber-500/20"
                  onClick={() => window.location.href = '/gouvernance'}>
                  <Settings className="w-4 h-4 mr-2" />
                  Configurer l'intégration
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Historique + OHGrid */}
          {/* ═══ #942 · IMPORTER UN EXPORT ANAH ═══════════════════════════
              🔑 Le RPA ne sert qu'à OBTENIR le fichier. Le login n'étant pas
              déterministe, JJ doit pouvoir déposer l'export à la main : la
              chaîne serveur qui suit est EXACTEMENT celle du RPA. */}
          <div className="rounded-xl p-4 mb-4"
               style={{ background: "var(--oh-bg-elevated)",
                        border: "0.5px solid var(--oh-border)" }}
               data-testid="bloc-import-anah">
            <div className="flex flex-wrap items-center gap-3">
              <span style={{ color: "var(--oh-text)", fontWeight: 500 }}>
                Importer un export ANAH
              </span>

              <input
                ref={champFichier}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                data-testid="champ-fichier-export"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setFichierChoisi(f);
                  setRapportImport(null);
                  // ⚠️ APERÇU d'office : JJ lit, PUIS il applique.
                  // ⛔ Jamais d'écriture au simple choix d'un fichier.
                  if (f) envoyerFichier(f, false);
                }}
              />

              <Button
                size="sm"
                disabled={importEnCours}
                onClick={() => champFichier.current?.click()}
                data-testid="btn-importer-export"
                style={{ background: "var(--oh-accent-solid)",
                         color: "var(--oh-accent-contrast)" }}
              >
                {importEnCours ? "Analyse en cours…" : "Choisir un fichier"}
              </Button>

              <label className="text-sm" htmlFor="limite-import"
                     style={{ color: "var(--oh-text-secondary)" }}>
                Périmètre témoin
              </label>
              <input
                id="limite-import"
                type="number"
                min="1"
                value={limiteImport}
                onChange={(e) => setLimiteImport(Number(e.target.value) || 0)}
                placeholder="nombre de dossiers"
                data-testid="champ-limite-import"
                className="w-24 rounded-lg px-2"
                style={{ height: 36, background: "var(--oh-bg-surface)",
                         color: "var(--oh-text)",
                         border: "0.5px solid var(--oh-border)" }}
              />
              <span className="text-sm" style={{ color: "var(--oh-text-muted)" }}>
                dossiers — laisser vide pour tout traiter
              </span>
            </div>

            {rapportImport && (
              <div className="mt-4" data-testid="rapport-import">
                <div className="rounded-lg p-3" style={{
                  background: rapportImport.verdict_fichier?.accepte && rapportImport.complete
                    ? "var(--oh-stat-green-bg)" : "var(--oh-stat-red-bg)",
                  color: rapportImport.verdict_fichier?.accepte && rapportImport.complete
                    ? "var(--oh-stat-green-text)" : "var(--oh-stat-red-text)",
                }}>
                  <div style={{ fontWeight: 500 }}>{rapportImport.fichierNom}</div>
                  {/* 🔑 Le verdict du FICHIER, avant tout chiffre : un fichier
                      refusé ou tronqué n'écrit rien, et ça se lit d'abord. */}
                  <div className="mt-1 text-sm">{rapportImport.completude}</div>
                  {rapportImport.verdict_fichier?.colonnes_manquantes?.length > 0 && (
                    <div className="mt-1 text-sm">
                      Colonnes absentes : {rapportImport.verdict_fichier.colonnes_manquantes.join(", ")}
                    </div>
                  )}
                </div>

                {rapportImport.lecture_du_zero && (
                  <p className="mt-2 text-sm" data-testid="lecture-du-zero"
                     style={{ color: "var(--oh-text-secondary)" }}>
                    {rapportImport.lecture_du_zero}
                  </p>
                )}

                {rapportImport.totaux && (
                  <div className="mt-2 flex flex-wrap gap-2 text-sm"
                       data-testid="totaux-import">
                    {Object.entries(rapportImport.totaux).map(([nature, n]) => (
                      <span key={nature} className="px-2 py-0.5 rounded"
                            style={{
                              background: (nature === "gele" || nature === "inconnu")
                                ? "var(--oh-stat-amber-bg)"
                                : nature === "rouvert" ? "var(--oh-stat-red-bg)"
                                : "var(--oh-stat-blue-bg)",
                              color: (nature === "gele" || nature === "inconnu")
                                ? "var(--oh-stat-amber-text)"
                                : nature === "rouvert" ? "var(--oh-stat-red-text)"
                                : "var(--oh-stat-blue-text)",
                            }}>
                        {nature} : {n}
                      </span>
                    ))}
                  </div>
                )}

                {rapportImport.verdict_fichier?.accepte && rapportImport.complete
                  && rapportImport.mode === "apercu" && (
                  <Button
                    size="sm"
                    className="mt-3"
                    disabled={importEnCours}
                    data-testid="btn-appliquer-import"
                    onClick={() => envoyerFichier(fichierChoisi, true)}
                    style={{ background: "var(--oh-accent-solid)",
                             color: "var(--oh-accent-contrast)" }}
                  >
                    Appliquer ces {rapportImport.dossiers_examines} dossier(s)
                  </Button>
                )}

                {rapportImport.mode === "application" && (
                  <p className="mt-3 text-sm" style={{ color: "var(--oh-text)" }}>
                    {rapportImport.dossiers_ecrits} dossier(s) écrit(s).
                    {rapportImport.refuses?.length > 0 &&
                      ` ${rapportImport.refuses.length} refus (gelé ou statut inconnu).`}
                  </p>
                )}
              </div>
            )}

            {exportsConserves.length > 0 && (
              <details className="mt-4" data-testid="exports-conserves">
                <summary className="cursor-pointer text-sm select-none"
                         style={{ color: "var(--oh-accent-text)" }}>
                  Exports conservés ({exportsConserves.length})
                </summary>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-sm min-w-[480px]">
                    <thead>
                      <tr style={{ color: "var(--oh-text-muted)" }}>
                        <th className="text-left py-1">Date</th>
                        <th className="text-left py-1">Lignes</th>
                        <th className="text-left py-1">Taille</th>
                        <th className="text-left py-1">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exportsConserves.map((ex) => (
                        <tr key={ex.nom} style={{ color: "var(--oh-text-secondary)" }}>
                          <td className="py-1">{formatDate(ex.modifie_le)}</td>
                          <td className="py-1">{ex.lignes ?? "?"}</td>
                          <td className="py-1">{Math.round(ex.taille_octets / 1024)} ko</td>
                          <td className="py-1">
                            {/* 🔴 La SOURCE : déposé à la main ou téléchargé par
                                le robot — ce n'est pas la même valeur de preuve. */}
                            <span className="px-2 py-0.5 rounded" style={{
                              background: ex.source === "rpa"
                                ? "var(--oh-stat-purple-bg)" : "var(--oh-stat-cyan-bg)",
                              color: ex.source === "rpa"
                                ? "var(--oh-stat-purple-text)" : "var(--oh-stat-cyan-text)",
                            }}>
                              {ex.source}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>

          <div className="flex justify-end">
            <Dialog open={showLogs} onOpenChange={setShowLogs}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="border-slate-600 text-slate-300" data-testid="btn-history">
                  <Clock className="w-4 h-4 mr-2" />
                  Historique des sync
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-white">Historique des synchronisations</DialogTitle>
                  <DialogDescription className="text-slate-400">Dernières synchronisations ANAH effectuées</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {syncLogs.length === 0 ? (
                    <p className="text-slate-400 text-center py-4">Aucune synchronisation effectuée</p>
                  ) : (
                    syncLogs.map((log, idx) => (
                      <div key={idx}
                        className={`p-3 rounded-lg border ${
                          log.status === 'success' ? 'bg-green-500/10 border-green-500/30' :
                          log.status === 'failed' ? 'bg-red-500/10 border-red-500/30' :
                          'bg-slate-700/50 border-slate-600'
                        }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {log.status === 'success' ? <CheckCircle className="w-4 h-4 text-green-400" /> :
                              log.status === 'failed' ? <XCircle className="w-4 h-4 text-red-400" /> :
                              <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                            <span className="text-white font-medium">
                              {log.status === 'success' ? 'Réussie' : log.status === 'failed' ? 'Échouée' : 'En cours'}
                            </span>
                          </div>
                          <span className="text-sm text-slate-400">{formatDate(log.started_at)}</span>
                        </div>
                        <div className="mt-2 text-sm text-slate-300">
                          {log.dossiers_synced} dossiers synchronisés / {log.dossiers_found} trouvés
                        </div>
                        {log.errors?.length > 0 && <div className="mt-2 text-sm text-red-400">{log.errors.join(", ")}</div>}
                        {/* 🔴 #941 — le parcours, dépliable SOUS le résultat.
                            JJ doit voir ce que le robot a fait sans ouvrir un
                            terminal. */}
                        <ParcoursRPA etapes={log.etapes} />
                      </div>
                    ))
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* OHGrid unifié : stats cliquables + filtres + tri + pagination + actions */}
          <OHGrid
            data={filteredDossiers}
            columns={columns}
            stats={ohStats}
            advancedFilters={advancedFilters}
            onAdvancedFilter={handleAdvFilter}
            rowActions={rowActions}
            bulkActions={bulkActions}
            selectable={true}
            pagination={true}
            pageSize={50}
            loading={false}
            onRefresh={() => { loadDossiers(); loadStats(); }}
            onRowClick={openDetail}
            searchPlaceholder="Rechercher par nom, référence ANAH, OPAL ou adresse..."
            searchFilter={searchFilter}
            defaultSortKey="date_depot"
            defaultSortDir="desc"
            emptyMessage={
              connectionStatus?.last_sync
                ? "Aucun dossier trouvé avec ces critères"
                : "Lancez une synchronisation pour récupérer les dossiers"
            }
            testIdPrefix="anah"
          />

          {/* Info isolation */}
          <Card className="bg-slate-800/30 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-400">
                  <p className="font-medium text-slate-300">Module isolé - Aucun impact sur le workflow</p>
                  <p className="mt-1">
                    Ce module synchronise les données depuis le portail ANAH à titre consultatif uniquement.
                    Les statuts affichés ici ne modifient pas automatiquement vos dossiers internes.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Modal Fiche détaillée */}
          <Dialog open={showDetailModal} onOpenChange={(open) => {
            setShowDetailModal(open);
            if (!open) { setLinkedDossierOH(null); setIsEditing(false); }
          }}>
            <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl flex items-center gap-3">
                  <FileText className="w-5 h-5 text-teal-400" />
                  Fiche Dossier ANAH
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Référence : {selectedDossier?.reference_anah}
                </DialogDescription>
              </DialogHeader>

              {selectedDossier && (
                <div className="space-y-6 mt-4">
                  {/* Bandeau Liaison OH */}
                  {linkedDossierOH ? (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          <div>
                            <p className="text-green-300 font-medium">Dossier OH lié</p>
                            <p className="text-green-200/70 text-sm">Réf: {linkedDossierOH.référence || linkedDossierOH.id}</p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline"
                          onClick={() => { window.location.href = `/commercial/dossier/${linkedDossierOH.id || linkedDossierOH._id}`; }}
                          className="border-green-500/50 text-green-300 hover:bg-green-500/20">
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Voir dossier
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-400" />
                          <div>
                            <p className="text-amber-300 font-medium">Aucun dossier OH lié</p>
                            <p className="text-amber-200/70 text-sm">Créez un dossier OH pour suivre ce client</p>
                          </div>
                        </div>
                        <Button size="sm" onClick={createDossierFromAnah} disabled={creatingDossier} className="bg-teal-600 hover:bg-teal-700">
                          {creatingDossier ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FolderPlus className="w-4 h-4 mr-1" />}
                          Créer dossier OH
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Demandeur */}
                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                    <h3 className="text-sm font-medium text-slate-400 mb-3 uppercase tracking-wider">Demandeur (Bénéficiaire)</h3>
                    <div className="space-y-2">
                      <p className="text-lg font-semibold text-white">{selectedDossier.nom_client || "-"}</p>
                      {selectedDossier.adresse && (
                        <p className="text-slate-300 text-sm">
                          {/* Fix [object Object] : adresse peut être dict */}
                          {typeof selectedDossier.adresse === "string"
                            ? selectedDossier.adresse
                            : [
                                selectedDossier.adresse?.rue,
                                [selectedDossier.adresse?.code_postal, selectedDossier.adresse?.ville].filter(Boolean).join(" "),
                              ].filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Références */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                      <h3 className="text-xs font-medium text-slate-400 mb-1 uppercase">Réf. ANAH</h3>
                      <p className="font-mono text-lg text-white">{selectedDossier.reference_anah}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                      <h3 className="text-xs font-medium text-slate-400 mb-1 uppercase">Réf. OPAL</h3>
                      <p className="font-mono text-lg text-white">{selectedDossier.reference_opal || "-"}</p>
                    </div>
                  </div>

                  {/* Statut */}
                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                    <h3 className="text-xs font-medium text-slate-400 mb-2 uppercase">Statut actuel</h3>
                    <Badge className={`${(STATUT_COLORS[selectedDossier.statut_normalise] || STATUT_COLORS.inconnu).bg} ${(STATUT_COLORS[selectedDossier.statut_normalise] || STATUT_COLORS.inconnu).text} ${(STATUT_COLORS[selectedDossier.statut_normalise] || STATUT_COLORS.inconnu).border} border text-sm px-3 py-1`}>
                      {STATUT_LABELS[selectedDossier.statut_normalise] || selectedDossier.statut_anah_label}
                    </Badge>
                  </div>

                  {/* Infos */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <h3 className="text-xs font-medium text-slate-400 uppercase">Dispositif</h3>
                        <p className="text-white mt-1">{selectedDossier.aide_type || "-"}</p>
                      </div>
                      <div>
                        <h3 className="text-xs font-medium text-slate-400 uppercase">Date de dépôt</h3>
                        <p className="text-white mt-1">{formatDateFR(selectedDossier.date_depot)}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <h3 className="text-xs font-medium text-slate-400 uppercase">Mandataire</h3>
                        <p className="text-white mt-1">{selectedDossier.mandataire || "-"}</p>
                      </div>
                      <div>
                        <h3 className="text-xs font-medium text-slate-400 uppercase">Opérateur</h3>
                        <p className="text-white mt-1">{selectedDossier.accompagnateur || "-"}</p>
                      </div>
                    </div>
                  </div>

                  {/* Infos éditables */}
                  <div className="border-t border-slate-700 pt-4 mt-2">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-teal-400 flex items-center gap-2">
                        <Edit3 className="w-4 h-4" />
                        Informations complémentaires
                      </h3>
                      {!isEditing ? (
                        <Button size="sm" variant="ghost" onClick={() => {
                          setIsEditing(true);
                          setEditData({
                            subvention_accordee: selectedDossier.manual_data?.subvention_manuelle || selectedDossier.subvention_accordee || '',
                            contact_telephone: selectedDossier.manual_data?.telephone || selectedDossier.contact_telephone || '',
                            notes: selectedDossier.manual_data?.notes || selectedDossier.notes || '',
                          });
                        }} className="text-slate-400 hover:text-white">
                          <Edit3 className="w-4 h-4 mr-1" />
                          Modifier
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)} className="text-slate-400">Annuler</Button>
                          <Button size="sm" onClick={saveManualData} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                            Enregistrer
                          </Button>
                        </div>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-slate-400 text-xs uppercase">Subvention accordée (€)</Label>
                            <div className="relative mt-1">
                              <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400" />
                              <Input type="number" placeholder="15000"
                                value={editData.subvention_accordee || ''}
                                onChange={(e) => setEditData(prev => ({ ...prev, subvention_accordee: e.target.value ? parseFloat(e.target.value) : null }))}
                                className="bg-slate-800 border-slate-600 pl-9" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-slate-400 text-xs uppercase">Téléphone demandeur</Label>
                            <div className="relative mt-1">
                              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400" />
                              <Input type="tel" placeholder="06 12 34 56 78"
                                value={editData.contact_telephone || ''}
                                onChange={(e) => setEditData(prev => ({ ...prev, contact_telephone: e.target.value }))}
                                className="bg-slate-800 border-slate-600 pl-9" />
                            </div>
                          </div>
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs uppercase">Notes / Commentaires</Label>
                          <Textarea placeholder="Informations complémentaires sur ce dossier..."
                            value={editData.notes || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                            className="bg-slate-800 border-slate-600 mt-1 min-h-[80px]" />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/50">
                          <div className="flex items-center gap-2 text-slate-400 text-xs uppercase mb-1">
                            <Euro className="w-3 h-3" />
                            Subvention accordée
                          </div>
                          <p className="text-emerald-400 font-semibold">
                            {(selectedDossier.manual_data?.subvention_manuelle || selectedDossier.subvention_accordee)
                              ? `${(selectedDossier.manual_data?.subvention_manuelle || selectedDossier.subvention_accordee).toLocaleString('fr-FR')} €`
                              : <span className="text-zinc-400 font-normal">Non renseigné</span>}
                          </p>
                        </div>
                        <div className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/50">
                          <div className="flex items-center gap-2 text-slate-400 text-xs uppercase mb-1">
                            <Phone className="w-3 h-3" />
                            Téléphone
                          </div>
                          <p className="text-white">
                            {(selectedDossier.manual_data?.telephone || selectedDossier.contact_telephone)
                              || <span className="text-zinc-400">Non renseigné</span>}
                          </p>
                        </div>
                        {(selectedDossier.manual_data?.notes || selectedDossier.notes) && (
                          <div className="col-span-2 bg-slate-800/30 rounded-lg p-3 border border-slate-700/50">
                            <div className="text-slate-400 text-xs uppercase mb-1">Notes</div>
                            <p className="text-slate-300 text-sm whitespace-pre-wrap">
                              {selectedDossier.manual_data?.notes || selectedDossier.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button variant="outline" onClick={() => { setShowDetailModal(false); setIsEditing(false); }}
                      className="border-slate-600">Fermer</Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
